/**
 * The planning grid as CSV — ADR 9010's "every table offers the CSV", applied to the one table
 * that is not a `DataTable`.
 *
 * Every row, children included whether or not their parent is open, in the order the grid is
 * sorted into, with a `depth` column so the hierarchy survives the file. reqs/00 D8's kinds of
 * nothing survive the crossing as they do on screen: a counted zero leaves as `0` — it was
 * measured — while not-ranged and not-reported leave as their own `missing` states, never as a
 * zero and never as each other. Nothing here is aggregated: a total row is the platform's row,
 * exported as it is drawn (ADR 0097).
 */

import type { TFunction } from 'i18next';

import {
  NOT_PUBLISHED, NO_PRINCIPAL, buildVersion,
  type ExportCell, type ExportColumn, type ExportRequest,
} from '../../export';
import type { CellValue, MeasureDefinition } from '../types';
import type { GridRow } from './PlanningGrid';

function measureCell(value: CellValue | undefined, measure: MeasureDefinition): ExportCell {
  if (value === undefined) return { kind: 'missing', state: 'not-reported' };
  switch (value.kind) {
    case 'number':
      return measure.currency === undefined
        ? { kind: 'number', raw: value.value }
        : { kind: 'money', raw: value.value, currency: measure.currency };
    case 'counted-zero':
      return measure.currency === undefined
        ? { kind: 'number', raw: 0 }
        : { kind: 'money', raw: 0, currency: measure.currency };
    case 'text':
      return { kind: 'text', value: value.value };
    case 'not-ranged':
      return { kind: 'missing', state: 'not-ranged' };
    case 'not-reported':
      return { kind: 'missing', state: 'not-reported' };
    default: {
      const exhaustive: never = value;
      return exhaustive;
    }
  }
}

export function gridRequest({ id, titleKey, rows, measures, t }: {
  readonly id: string;
  readonly titleKey: string;
  /** Every row, flattened in display order — the caller's sorted model. */
  readonly rows: readonly GridRow[];
  readonly measures: readonly MeasureDefinition[];
  readonly t: TFunction;
}): ExportRequest {
  const columns: ExportColumn[] = [
    { key: 'entity', headerKey: 'grid.column.entity', kind: 'text' },
    { key: 'depth', headerKey: 'grid.column.depth', kind: 'integer' },
    ...measures.map((measure): ExportColumn => ({
      key: measure.key,
      headerKey: measure.labelKey,
      kind: measure.currency === undefined ? 'number' : 'money',
      unit: t(measure.unitKey),
      polarity: measure.polarity,
      ...(measure.currency === undefined ? {} : { currency: measure.currency }),
    })),
  ];
  const body: ExportCell[][] = rows.map((row) => [
    { kind: 'text', value: row.label },
    { kind: 'integer', raw: row.depth },
    ...measures.map((measure) => measureCell(row.values[measure.key], measure)),
  ]);
  return {
    id,
    titleKey,
    columns,
    rows: () => body,
    rowCount: body.length,
    provenance: {
      filters: [],
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: measures.map((measure) => ({
        measure: measure.key,
        definition: t(measure.labelKey),
        registerVersion: measure.registerVersion,
      })),
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: {
      filters: [],
      sort: [],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}
